import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { submitInquiry } from '../services/inquiryService';
import './InquiryModal.css';

const initialForm = {
  name: '',
  phone: '',
  address: '',
  consent: false,
};

function InquiryModal({ open, onClose }) {
  const titleId = useId();
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      setForm(initialForm);
      setSubmitted(false);
      setSubmitting(false);
      setError('');
    }
  }, [open]);

  if (!open) return null;

  const update = (field) => (e) => {
    const value = field === 'consent' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.address.trim()) {
      setError('이름, 전화번호, 주소를 모두 입력해 주세요.');
      return;
    }
    if (!form.consent) {
      setError('개인정보 수집·이용에 동의해 주세요.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await submitInquiry({
        name: form.name.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.message || '문의 접수 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="inquiry-modal" role="presentation" onClick={onClose}>
      <div
        className="inquiry-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="inquiry-modal__close" onClick={onClose} aria-label="닫기">
          ×
        </button>

        {submitted ? (
          <div className="inquiry-modal__done">
            <h2 id={titleId}>문의가 접수되었습니다</h2>
            <p>
              {form.name}님, 입력해 주신 연락처로 빠른 시일 내에 안내드리겠습니다.
            </p>
            <button type="button" className="btn btn--primary" onClick={onClose}>
              확인
            </button>
          </div>
        ) : (
          <form className="inquiry-modal__form" onSubmit={handleSubmit}>
            <p className="inquiry-modal__eyebrow">i-LOG Booth</p>
            <h2 id={titleId}>아이로그 부스 도입 문의</h2>
            <p className="inquiry-modal__desc">
              도입을 검토 중이신 교육기관·공간 담당자분의 정보를 남겨 주세요.
            </p>

            <label>
              이름
              <input
                type="text"
                name="name"
                autoComplete="name"
                placeholder="홍길동"
                value={form.name}
                onChange={update('name')}
                required
                disabled={submitting}
              />
            </label>

            <label>
              전화번호
              <input
                type="tel"
                name="phone"
                autoComplete="tel"
                placeholder="010-0000-0000"
                value={form.phone}
                onChange={update('phone')}
                required
                disabled={submitting}
              />
            </label>

            <label>
              주소
              <input
                type="text"
                name="address"
                autoComplete="street-address"
                placeholder="도입 예정 장소 주소"
                value={form.address}
                onChange={update('address')}
                required
                disabled={submitting}
              />
            </label>

            <label className="inquiry-modal__consent">
              <input
                type="checkbox"
                checked={form.consent}
                onChange={update('consent')}
                disabled={submitting}
              />
              <span>
                <Link to="/consent" target="_blank" rel="noreferrer">
                  개인정보 수집·이용 동의
                </Link>
                에 동의합니다. (필수)
              </span>
            </label>

            {error ? <p className="inquiry-modal__error">{error}</p> : null}

            <button
              type="submit"
              className="btn btn--primary inquiry-modal__submit"
              disabled={submitting}
            >
              {submitting ? '접수 중…' : '문의 보내기'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default InquiryModal;
